/**
 * DeCAs externos — the documents somebody **else** issued.
 *
 * A company that is the other party to a transport still owes the one-year retention duty (BOE
 * Second.4): where the shipper generated the DeCA in its own system, it is enough that this
 * company can download it for a year. But BOE Third.5 lets the issuing party disable its URL
 * seven days after the service, so a stored link stops discharging the duty long before the year
 * is out — which is why the PDF can be attached and the platform then keeps the bytes.
 *
 * **These are not our documents**, and the screen says so by what it does not have: no CSV, no
 * estado, no version chain, no QR of ours, no modification lifecycle, and a plain delete rather
 * than the void lifecycle `/decas` uses. A mis-pasted URL is a mistake, not a legal record.
 *
 * The URL is rendered as an ordinary external link and **never** wrapped or proxied. The portal
 * standing in front of another party's download would be the same mistake BOE Third.3–4 forbids
 * us on our own documents, made on somebody else's. A record made from its PDF alone has no URL and
 * shows «Sin enlace» (#184).
 *
 * Read is `readsDecas`; recording and deleting are `writeDecas` — a viewer sees the list and no
 * form. Both are affordances: the server scopes every call to the caller's organisation and
 * answers an out-of-role write with 403 whatever this screen draws.
 */

import { useState } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { unwrap } from '@deca/api'

import { api } from '../app/api.ts'
import { apiErrorMessage } from '../app/errors.ts'
import { createExternalDecaWithPdf, type ExternalDecaReference } from '../app/externalDecas.ts'
import { qk, useExternalDecas } from '../app/queries.ts'
import { useAuth } from '../app/auth.tsx'
import { abilitiesFor } from '../deca/roles.ts'
import { formatDateTime } from '../deca/format.ts'
import { AppShell } from '../shell/AppShell.tsx'
import { Button, Card, EmptyState, NoAccessState, Notice } from '../ui/primitives.tsx'
import { DataTable, Pagination, type Column } from '../ui/DataTable.tsx'
import { ArchivedCopyTag } from '../ui/EstadoChip.tsx'
import { Icon } from '../ui/Icon.tsx'
import { TextInput } from '../ui/form.tsx'
import { PdfDropZone, useDroppedFilesStayOnPage } from '../ui/PdfDropZone.tsx'
import { useToast } from '../ui/Toast.tsx'
import { useStepBackPastEnd } from '../ui/useStepBackPastEnd.ts'
import { PAGE_DEFAULT, pagedSearchSchema, PER_PAGE } from './searchSchemas.ts'

interface ExternalDecaFormValues {
  url: string
  label: string
}

export function ExternalDecasScreen() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const client = useQueryClient()
  const { principal } = useAuth()
  const abilities = abilitiesFor(principal)

  // Page and search live in the URL, never in component state, so a filtered list is a link.
  const search = pagedSearchSchema.parse(useSearch({ strict: false }))
  const list = useExternalDecas(
    { page: search.page ?? PAGE_DEFAULT, perPage: PER_PAGE, ...(search.q ? { q: search.q } : {}) },
    abilities.readsDecas,
  )
  // A page past the end shows the last page, not «Todavía no hay DeCAs externas» (#165).
  useStepBackPastEnd(list.data?.meta, search.page ?? PAGE_DEFAULT, (page) => {
    void navigate({ to: '/decas-externas', search: { ...search, page }, replace: true })
  })

  const form = useForm<ExternalDecaFormValues>({ defaultValues: { url: '', label: '' } })
  const [file, setFile] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  // A record needs the URL, the PDF or both (#184), so «Registrar» waits for one of the two.
  const canRegister = form.watch('url').trim() !== '' || file !== null
  // For a viewer too, who has no zone: a PDF dropped on the list must not take the portal away.
  useDroppedFilesStayOnPage()

  // Every hook above the early return — the rule this portal keeps, because a conditional
  // return placed one line higher changes the hook order on the render where the principal
  // arrives.
  if (!abilities.readsDecas) {
    return (
      <AppShell title={t('externalDecas.title')}>
        <Card>
          <NoAccessState />
        </Card>
      </AppShell>
    )
  }

  async function submit(values: ExternalDecaFormValues) {
    setBusy(true)
    setError(null)
    try {
      const url = values.url.trim() || null
      // Empty means "no note", not an empty note — the contract's `label` is nullable.
      const label = values.label.trim() || null
      // With a copy, one multipart request: the server writes the record and its copy together, so a
      // failed upload leaves nothing behind (#184). Without one, the URL alone, as JSON.
      if (file) await createExternalDecaWithPdf(file, { url, label })
      else if (url) await unwrap(api.POST('/api/v1/external-decas', { body: { url, label } }))
      else return // «Registrar» is off with neither, so this is not reached
      form.reset({ url: '', label: '' })
      setFile(null)
      void client.invalidateQueries({ queryKey: qk.externalDecas() })
      toast.success(t('externalDecas.added'))
    } catch (caught) {
      setError(apiErrorMessage(caught, t))
    } finally {
      setBusy(false)
    }
  }

  async function remove(row: ExternalDecaReference) {
    if (!window.confirm(t('externalDecas.deleteConfirm'))) return
    setDeletingId(row.id)
    setError(null)
    try {
      await unwrap(
        api.DELETE('/api/v1/external-decas/{externalDecaId}', {
          params: { path: { externalDecaId: row.id } },
        }),
      )
      void client.invalidateQueries({ queryKey: qk.externalDecas() })
      toast.success(t('externalDecas.deleted'))
    } catch (caught) {
      setError(apiErrorMessage(caught, t))
    } finally {
      setDeletingId(null)
    }
  }

  const columns: Column<ExternalDecaReference>[] = [
    {
      key: 'reference',
      header: t('externalDecas.columns.reference'),
      render: (row) => (
        <Link to="/decas-externas/$externalDecaId" params={{ externalDecaId: row.id }}>
          {row.label ?? t('externalDecas.noLabel')}
        </Link>
      ),
    },
    {
      key: 'url',
      header: t('externalDecas.columns.url'),
      render: (row) =>
        row.url === null ? (
          // Made from its PDF alone (#184): nothing to link out to. The copy is on its detail screen.
          <span className="small muted">{t('externalDecas.noLink')}</span>
        ) : (
          // Straight out to the issuing party. Never `/api/...`, never `/d/...`, never a screen of
          // ours in between. The accessible name is the URL itself, so the visible text and the
          // name agree (WCAG 2.5.3); the catalogue sentence is the tooltip.
          <a
            className="small row mono"
            href={row.url}
            target="_blank"
            rel="noreferrer noopener"
            title={t('externalDecas.openExternal')}
            style={{ gap: 'var(--space-xs)', wordBreak: 'break-all' }}
          >
            {row.url}
            <Icon name="externalLink" size={14} />
          </a>
        ),
    },
    {
      key: 'copy',
      header: t('externalDecas.columns.copy'),
      // At the end of a phone card's heading (#111): whether a copy is held is what this list is for.
      card: 'end',
      render: (row) => <ArchivedCopyTag archived={row.archivedAt !== null} />,
    },
    {
      key: 'importedAt',
      header: t('externalDecas.columns.importedAt'),
      numeric: true,
      render: (row) => formatDateTime(row.importedAt, i18n.language),
    },
    {
      key: 'importedBy',
      header: t('externalDecas.columns.importedBy'),
      render: (row) => <span className="small muted">{row.importedBy.display}</span>,
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'right',
      render: (row) =>
        abilities.writeDecas ? (
          <div className="table__actions">
            <Button
              size="sm"
              variant="ghost"
              icon="trash"
              disabled={deletingId === row.id}
              onClick={() => void remove(row)}
            >
              {t('common.delete')}
            </Button>
          </div>
        ) : null,
    },
  ]

  return (
    <AppShell title={t('externalDecas.title')} subtitle={t('externalDecas.subtitle')}>
      {error ? <Notice tone="danger">{error}</Notice> : null}

      <Notice tone="info" title={t('externalDecas.notOurs')}>
        {t('externalDecas.retentionNotice')}
      </Notice>

      {abilities.writeDecas ? (
        <Card title={t('externalDecas.addTitle')}>
          {/*
            `.card__body` sets padding and no gap, so these three blocks are siblings that
            would otherwise touch — the file button ended up flush against the submit button.
            Wrapping them is how every other multi-block card in the portal spaces itself.
          */}
          <div className="stack">
            <div className="form-grid form-grid--full">
              <TextInput
                label={t('externalDecas.url')}
                type="url"
                inputMode="url"
                placeholder="https://"
                help={t('externalDecas.urlHelp')}
                error={form.formState.errors.url?.message}
                {...form.register('url')}
              />
              <TextInput
                label={t('externalDecas.label')}
                help={t('externalDecas.labelHelp')}
                error={form.formState.errors.label?.message}
                {...form.register('label')}
              />
            </div>

            <div className="stack-sm">
              <span className="field__label">{t('externalDecas.pdfLabel')}</span>
              {/* Chosen or dropped (#225), by the same checks: a 40 MB scan is refused before it is
                  uploaded, and `EXTERNAL_DECA_TOO_LARGE` stays the authority. */}
              <PdfDropZone
                label={t('externalDecas.choosePdf')}
                busy={busy}
                onFile={(chosen) => {
                  setError(null)
                  setFile(chosen)
                }}
              >
                {file ? (
                  <>
                    {/* A scan's name has no break in it: it wraps rather than widen the page (#111). */}
                    <span className="small mono external-decas__file-name">{file.name}</span>
                    <Button size="sm" variant="ghost" icon="x" disabled={busy} onClick={() => setFile(null)}>
                      {t('externalDecas.clearPdf')}
                    </Button>
                  </>
                ) : (
                  <span className="small muted">{t('externalDecas.pdfHelp')}</span>
                )}
              </PdfDropZone>
            </div>

            <div className="row">
              <Button
                variant="primary"
                icon="plus"
                loading={busy}
                disabled={busy || !canRegister}
                onClick={() => void form.handleSubmit(submit)()}
              >
                {t('externalDecas.add')}
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      <Card
        title={t('externalDecas.listTitle')}
        flush
        actions={
          <form
            className="topbar__search"
            role="search"
            onSubmit={(event) => {
              event.preventDefault()
              const value = new FormData(event.currentTarget).get('q')
              const q = typeof value === 'string' && value.trim() ? value.trim() : undefined
              void navigate({ to: '/decas-externas', search: { q, page: 1 } })
            }}
          >
            <Icon name="search" size={16} />
            <input
              className="input"
              type="search"
              name="q"
              defaultValue={search.q ?? ''}
              placeholder={t('externalDecas.searchLabel')}
              aria-label={t('externalDecas.searchLabel')}
            />
          </form>
        }
      >
        <DataTable
          rows={list.data?.data}
          columns={columns}
          rowKey={(row) => row.id}
          caption={t('externalDecas.listTitle')}
          isLoading={list.isLoading}
          error={list.error}
          onRetry={() => void list.refetch()}
          // Not `DataTable`'s default: "Sin resultados" reads as "your filter matched nothing",
          // which is the wrong answer on a surface whose real one is "you have recorded none".
          empty={
            <EmptyState
              icon="externalLink"
              title={t('externalDecas.emptyTitle')}
              body={t('externalDecas.emptyBody')}
            />
          }
        />
        {list.data?.meta ? (
          <Pagination
            page={list.data.meta.page}
            perPage={list.data.meta.perPage}
            total={list.data.meta.total}
            totalPages={list.data.meta.totalPages}
            onPage={(page) =>
              void navigate({ to: '/decas-externas', search: { ...search, page } })
            }
          />
        ) : null}
      </Card>
    </AppShell>
  )
}
